import React, { useEffect, useState } from 'react';
import { useRoute, Link } from 'wouter';
import { ExternalLink, ArrowLeft, Check, ChevronRight, ArrowRight } from 'lucide-react';
import { tools } from '../../utils/tools';
import { SEO } from '../SEO';
import { Navigation } from '../Layout/Navigation';
import { RelatedTools } from './RelatedTools';
import { generateCategorySlug, generateToolSlug } from '../../utils/slugs';
import { trackToolClick } from '../../utils/trackToolClick';
import { ReviewsSection } from './ReviewsSection';
import { fetchToolReviewStats, type ToolReviewStats } from '../../utils/reviews';

export function ToolDetailPage() {
  const [match, params] = useRoute('/tools/:toolSlug');
  const toolSlug = params?.toolSlug;
  const tool = tools.find(t =>
    t.id === toolSlug ||
    t.name.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '') === toolSlug
  );

  const [reviewStats, setReviewStats] = useState<ToolReviewStats | null>(null);
  useEffect(() => {
    if (!tool) {
      setReviewStats(null);
      return;
    }
    fetchToolReviewStats(tool.id).then(setReviewStats);
  }, [tool?.id]);

  if (!match) return null;

  if (!tool) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <Navigation />
        <div className="max-w-2xl mx-auto py-16 px-4 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Tool Not Found</h1>
          <p className="text-gray-600 mb-8">We couldn't find the tool you're looking for. It may have been renamed or removed.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
            <Link href="/tools">
              <a className="flex items-center justify-center gap-2 px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-700 hover:border-blue-300 hover:text-blue-600 transition-all text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500">
                <ArrowLeft className="w-4 h-4" />
                Browse All Tools
              </a>
            </Link>
            <Link href="/directory">
              <a className="flex items-center justify-center gap-2 px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-700 hover:border-blue-300 hover:text-blue-600 transition-all text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500">
                Browse by Category
              </a>
            </Link>
            <Link href="/questionnaire">
              <a className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                Get Recommendations
                <ArrowRight className="w-4 h-4" />
              </a>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const seoTitle = `${tool.name} Review 2026 - Features, Pricing & Alternatives | btoolme`;
  const rawDescription = `${tool.description} Compare ${tool.name} features, pricing, and alternatives. See if ${tool.name} is right for your business.`;
  const seoDescription = rawDescription.length > 155 ? rawDescription.slice(0, 152) + '...' : rawDescription;

  const toolKeywords = [
    tool.name,
    `${tool.name} review`,
    `${tool.name} pricing`,
    `${tool.name} alternatives`,
    `${tool.name} features`,
    `best ${tool.category.toLowerCase()} software`,
    tool.category,
    ...(tool.bestFor || []).map(b => `${tool.name} for ${b.replace(/-/g, ' ')}`),
    ...(tool.industries || []).slice(0, 3).map(i => `${tool.category.toLowerCase()} for ${i.replace(/-/g, ' ')}`)
  ].join(', ');

  const lastUpdatedIso = new Date().toISOString();
  const lastUpdatedDisplay = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const productSchema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": tool.name,
    "description": tool.description,
    "applicationCategory": "BusinessApplication",
    "applicationSubCategory": tool.category,
    "url": tool.website,
    "sameAs": `https://btoolme.com/tools/${toolSlug}`,
    "operatingSystem": "Web, iOS, Android",
    "image": tool.imageUrl || "https://btoolme.com/social-preview.png",
    "dateModified": lastUpdatedIso,
    "publisher": {
      "@type": "Organization",
      "name": "btoolme",
      "url": "https://btoolme.com",
      "logo": "https://btoolme.com/favicon.svg"
    },
    "offers": {
      "@type": "Offer",
      "price": tool.pricing.includes('free') ? "0" : tool.pricing.includes('medium') ? "20" : "100",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    },
    "featureList": tool.features?.slice(0, 10).map(f => f.replace(/-/g, ' ')) || []
  };

  if (reviewStats && reviewStats.review_count > 0) {
    productSchema.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": Number(reviewStats.avg_rating),
      "reviewCount": reviewStats.review_count,
      "bestRating": 5,
      "worstRating": 1,
    };
  }

  const categorySlug = generateCategorySlug(tool.category);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <SEO
        title={seoTitle}
        description={seoDescription}
        path={`/tools/${toolSlug}`}
        schema={productSchema}
        type="article"
        keywords={toolKeywords}
        imageUrl="https://btoolme.com/social-preview.png"
        breadcrumbs={[
          { name: 'All Tools', path: '/tools' },
          { name: tool.category, path: `/categories/${categorySlug}` },
          { name: tool.name, path: `/tools/${toolSlug}` },
        ]}
      />

      <Navigation />

      <div className="max-w-4xl mx-auto py-8 px-4">
        {/* Breadcrumbs */}
        <nav className="flex items-center text-sm text-gray-600 mb-6" aria-label="Breadcrumb">
          <Link href="/">
            <a className="hover:text-blue-600 transition-colors">Home</a>
          </Link>
          <ChevronRight className="w-4 h-4 mx-2" />
          <Link href="/directory">
            <a className="hover:text-blue-600 transition-colors">Directory</a>
          </Link>
          <ChevronRight className="w-4 h-4 mx-2" />
          <Link href={`/categories/${categorySlug}`}>
            <a className="hover:text-blue-600 transition-colors">{tool.category}</a>
          </Link>
          <ChevronRight className="w-4 h-4 mx-2" />
          <span className="text-gray-900 font-medium">{tool.name}</span>
        </nav>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-6 sm:p-8">
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <span className="bg-blue-100 text-blue-700 text-sm font-medium px-3 py-1 rounded-full">
                  {tool.category}
                </span>
                <a
                  href={tool.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackToolClick(tool.id)}
                  aria-label={`Visit ${tool.name} official website (opens in new tab)`}
                  className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Visit Website
                  <ExternalLink className="w-4 h-4 ml-2" aria-hidden="true" />
                </a>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                {tool.name} Review ({new Date().getFullYear()})
              </h1>
              <p className="text-sm text-gray-500 mb-4">
                <time dateTime={lastUpdatedIso}>Updated {lastUpdatedDisplay}</time>
                {' '}&middot; Category: {tool.category}
              </p>
              <p className="text-lg text-gray-700 font-medium mb-3">
                {tool.name} is a {tool.category.toLowerCase()} tool for
                {tool.bestFor && tool.bestFor.length > 0
                  ? ' ' + tool.bestFor.slice(0, 2).map(b => b.replace(/-/g, ' ')).join(' and ')
                  : ' businesses'}.
              </p>
              <p className="text-base text-gray-600">{tool.description}</p>
            </div>

            {/* Key Features */}
            {tool.features && tool.features.length > 0 && (
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Key Features</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {tool.features.map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 capitalize">{feature.replace(/-/g, ' ')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Best For */}
            {tool.bestFor && tool.bestFor.length > 0 && (
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Best For</h2>
                <div className="flex flex-wrap gap-2">
                  {tool.bestFor.map((type, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm capitalize"
                    >
                      {type.replace(/-/g, ' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Industries */}
            {tool.industries && tool.industries.length > 0 && (
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Industries</h2>
                <div className="flex flex-wrap gap-2">
                  {tool.industries.map((industry, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm capitalize"
                    >
                      {industry.replace(/-/g, ' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Business Goals */}
            {tool.goals && tool.goals.length > 0 && (
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Helps You Achieve</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {tool.goals.map((goal, index) => (
                    <div key={index} className="flex items-start">
                      <Check className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 capitalize">{goal.replace(/-/g, ' ')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pricing */}
            {tool.pricing && tool.pricing.length > 0 && (
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Pricing</h2>
                <div className="flex flex-wrap gap-2">
                  {tool.pricing.map((price, index) => {
                    const priceLabels: Record<string, string> = {
                      'free': 'Free',
                      'low': 'Budget-Friendly',
                      'medium': 'Mid-Range',
                      'high': 'Premium'
                    };
                    return (
                      <span
                        key={index}
                        className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm"
                      >
                        {priceLabels[price] || price}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <div className="text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Ready to find your perfect tool stack?
                </h3>
                <p className="text-gray-600 mb-6">
                  Get personalized recommendations based on your business needs
                </p>
                <Link href="/questionnaire">
                  <a className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all">
                    Get My Recommendations
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </a>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <ReviewsSection toolId={tool.id} toolName={tool.name} />

        {/* Related Tools */}
        <RelatedTools currentTool={tool} allTools={tools} />
      </div>
    </div>
  );
}
